<template>
  <div class="time-range-container-250113163455">
    <!-- 开始时间选择器 -->
    <el-select
      v-model="compData.st"
      clearable
      :placeholder="$str.getKey('收益管理/请选择时间')"
      @change="handleTimeChange"
    >
      <!-- 时钟前缀 -->
      <template #prefix>
        <el-icon><Clock /></el-icon>
      </template>
      <!-- 下拉选项 -->
      <el-option v-for="item in compData.stOptions" :key="item.value" :label="item.label" :value="item.label" :disabled="item.disabled" />
    </el-select>

    <!-- 分隔符 -->
    <div class="separator-container">
      <!-- 在父组件中，可通过 <template #separator>-</template> 自定义分隔符 -->
      <slot name="separator">~</slot>
    </div>

    <!-- 结束时间选择器 -->
    <el-select
      v-model="compData.et"
      clearable
      :placeholder="$str.getKey('收益管理/请选择时间')"
      @change="handleTimeChange"
    >
      <!-- 时钟前缀 -->
      <template #prefix>
        <el-icon><Clock /></el-icon>
      </template>
      <!-- 下拉选项 -->
      <el-option v-for="item in compData.etOptions" :key="item.value" :label="item.label" :value="item.label" :disabled="item.disabled" />
    </el-select>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted, defineOptions, defineProps, useAttrs } from "vue"
import dayjs from 'dayjs';
import _ from "lodash";

const $str = window.$str;

/** 组件设置：事件名称声明 */
const emit = defineEmits(["update:modelValue", 'change']);


/** 组件设置：设置组件配置项 */
defineOptions({
  name: "TimeRangeSelect"
});

/** 属性传递：接收父组件传递下来的数据 */
const props = defineProps({
  modelValue: {
    type: Array,
    default: ['', '']
  },
  /** 约束：可选最小时间（例如上一行结束时间） */
  minTime: {
    type: String,
    default: ''
  },
  start: {
    type: String,
    default: '00:00'
  },
  step: {
    type: String,
    default: '00:30'
  },
  end: {
    type: String,
    default: '24:00'
  },
  includeEnd: {
    type: Boolean,
    default: false
  },
});





/** 数据：组件数据 */
const compData = reactive({
  st: props.modelValue[0],
  et: props.modelValue[1],
  stOptions: [],
  etOptions: [],
})



/** 监听：props中部分属性的改变，重新初始化组件 */
watch([() => props.start,() => props.step,() => props.end,() => props.includeEnd, () => props.minTime], () => {
  init();
},{immediate:true, deep:true});


/** 监听：父组件中通过v-model绑定到该组件的值的变化，重新设置compData的 st|et 值 */
watch(() => props.modelValue,(nv) => {
  compData.st = nv[0];
  compData.et = nv[1];
  refreshItemDisabledState(compData.st, compData.et);
},{immediate: true, deep: true})


/** ====== [业务：功能方法] =================================================================== */
/** 初始化：组件 */
function init() {
  initOptions(props.start, props.end, props.step, props.includeEnd);
  refreshItemDisabledState(compData.st, compData.et);
}



/** 初始化：下拉选项 */
function initOptions(stStr, etStr, step, isIncludeEnd) {
  const selectOptionList = [];
  const cfg = {
    st: transformStrToTime(stStr),
    et: transformStrToTime(etStr),
    step: 1,
  }

  const [stepH, stepM] = step.split(':');
  cfg.step = +stepH * 30 + +stepM;
  let tmpTime = cfg.st;

  // 获取【结束时间】之前对应的【时间选项】
  while(tmpTime.isBefore(cfg.et)) {
    const item = {
      value: tmpTime,
      label: tmpTime.format('HH:mm'),
      disabled: false,
    }
    selectOptionList.push(item);
    tmpTime = tmpTime.add(cfg.step, 'minute');
  }

  // 获取【结束时间】对应的【时间选项】
  if (isIncludeEnd) {
    const endItem = {
      value: cfg.et,
      label: cfg.et.format('HH:mm'),
    }
    if (cfg.et.format('HH:mm') === '00:00') {
      endItem.label = '24:00';
    }
    selectOptionList.push(endItem);
  }

  compData.stOptions = _.cloneDeep(selectOptionList);
  compData.etOptions = _.cloneDeep(selectOptionList);
}


/** 事件响应：选中项发生变化 */
function handleTimeChange() {
  const timeRange = [compData.st, compData.et]
  emit('update:modelValue', timeRange); // 派发事件，更新父组件通过v-model绑定的数据
  emit('change', timeRange, transformStrToTime); // 触发父组件注册的事件：time & 时间格式转换方法
  refreshItemDisabledState(compData.st, compData.et);
}





/** ====== [辅助：功能方法] =================================================================== */
/** 转换：将【时间字符串】转换为【时间】 */
function transformStrToTime(timeStr, refDate='1970-01-01') {
  if (!isValidDate(refDate)) {
    refDate = undefined;
  }
  if (timeStr === '24:00') {
    return dayjs(refDate).startOf('day').add(1, 'day');
  } else {
    if (timeStr) {
      const [hh, mm] = timeStr.split(':');
      return dayjs(refDate).set('hour', hh).set('minute',mm).set('second',0).set('millisecond',0);
    } else {
      return undefined;
    }
  }
}


/**
 * 判断：日期格式是否有效
 * @param {*} date 
 * @returns 
 */
function isValidDate(date) {
  return dayjs(date).isValid();
}


/** 更新：下拉选项的禁选状态 */
function refreshItemDisabledState(stLabel, etLabel) {
  const min = props.minTime ? transformStrToTime(props.minTime) : undefined

  const applyMin = (list) => {
    if (!min) return
    list.forEach((it) => {
      if (it?.value && it.value.isBefore(min)) {
        it.disabled = true
      }
    })
  }

  // 先应用最小值约束（上一行结束时间等）
  applyMin(compData.stOptions)
  applyMin(compData.etOptions)

  const stItem = compData.stOptions.find(it => it.label === stLabel);
  // 更新：开始时间下拉选项的禁选状态
  if (stItem) {
    compData.etOptions.forEach(etItem => {
      if (min && etItem?.value && etItem.value.isBefore(min)) {
        etItem.disabled = true;
        return;
      }
      if (etItem.value.isBefore(stItem.value)) {
        etItem.disabled = true;
      } else if (etItem.value.isAfter(stItem.value)) {
        etItem.disabled = false;
      } else {
        etItem.disabled = true;
      }
    })
  } else {
    compData.etOptions.forEach(etItem => {
      if (min && etItem?.value && etItem.value.isBefore(min)) {
        etItem.disabled = true;
      } else {
        etItem.disabled = false;
      }
    })
  }

  // 更新：结束时间下拉选项的禁选状态
  const etItem = compData.etOptions.find(it => it.label === etLabel);
  if (etItem) {
    compData.stOptions.forEach(stItem => {
      if (min && stItem?.value && stItem.value.isBefore(min)) {
        stItem.disabled = true;
        return;
      }
      if (stItem.value.isAfter(etItem.value)) {
        stItem.disabled = true;
      } else if (stItem.value.isBefore(etItem.value)) {
        stItem.disabled = false;
      } else {
        stItem.disabled = true;
      }
    })
  } else {
    compData.stOptions.forEach(stItem => {
      if (min && stItem?.value && stItem.value.isBefore(min)) {
        stItem.disabled = true;
      } else {
        stItem.disabled = false;
      }
    })
  }
}



</script>
<style lang="scss" scoped>
.time-range-container-250113163455 {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 10px;

  /** 设置选择输入框的高度，和父容器设定的高度一致 */
  .el-select { // ::v-deep 
    height: 100%;
    width: 112px;
    min-width: 112px;
    // .select-trigger, .el-input  {
    //   height: 100%;
    // }
  }

  .separator-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

}
</style>
